import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PickupPointRequestDto } from '@tiendanube/common';
import { RestrictionType } from '@tiendanube/common/src/enums/RestrictionType';
import { useGetProvinces } from 'domains/Shipping/Addresses/hooks';
import { getLocationsStatus } from 'domains/Shipping/Locations/locationSlice/locationSelectors';
import PickupBusinessDays from './components/PickupBusinessHours';
import {
  sortBusinessHours,
  getBusinessHoursWithIndex,
} from './components/PickupBusinessHours/utils';
import PickupCoverageCard from './components/PickupCoverageCard';
import PickupPointCost from './components/PickupPointCost';
import PickupReadyTime from './components/PickupReadyTime';
import SecondStepFormSkeleton from './Skeleton';
import usePickupPoints from '../../hooks/usePickupPoints';

interface SecondStepFormProps {
  errors: Partial<Record<string, string>>;
  values: PickupPointRequestDto;
  setFieldValue: (name: string, value: any) => void;
}

function SecondStepForm({
  values,
  errors,
  setFieldValue,
}: Readonly<SecondStepFormProps>): JSX.Element {
  const { isLoadingDetail } = usePickupPoints();
  const { isLoading: isLoadingLocation } = useSelector(getLocationsStatus);
  const isLoading = isLoadingLocation || isLoadingDetail;
  const { provinces } = useGetProvinces();

  const normalizedRestrictionArea = useMemo(() => {
    if (!values.restrictionArea || !provinces)
      return values.restrictionArea || [];

    return values.restrictionArea.map((zone) => {
      const province = provinces.find((p) => p.code === zone.provinceCode);
      if (province) {
        return {
          ...zone,
          provinceId: province.id,
        };
      }

      return zone;
    });
  }, [values.restrictionArea, provinces]);

  return (
    <>
      {isLoading && <SecondStepForm.Skeleton />}
      {!isLoading && (
        <>
          <PickupBusinessDays
            businessHours={sortBusinessHours(
              getBusinessHoursWithIndex(values.businessHours),
            )}
            errors={errors}
            setFieldValue={setFieldValue}
          />
          <PickupReadyTime
            time={values.estimateDeliveryTime}
            errors={errors}
            setFieldValue={setFieldValue}
          />
          <PickupPointCost
            cost={values.cost}
            errors={errors}
            setFieldValue={setFieldValue}
          />
          <PickupCoverageCard
            restrictionType={values.restrictionType || RestrictionType.COUNTRY}
            restrictionArea={normalizedRestrictionArea}
            range={values.range}
            errors={errors}
            setFieldValue={setFieldValue}
          />
        </>
      )}
    </>
  );
}

SecondStepForm.Skeleton = SecondStepFormSkeleton;
export default SecondStepForm;
